import React, {useEffect, useMemo, useRef, useState} from 'react';
import {isNumber} from 'lodash';
import {useMutation} from '@apollo/client';
import {Alert, Box, Grid} from '@mui/material';
import {JsonViewer} from '@textea/json-viewer';
import {TrendingUp} from '@mui/icons-material';
import {addBreadcrumb} from '@sentry/react';
import {useSnackbar} from 'notistack';
import {useAccountContext} from '../../contexts/AccountContext';
import {useReviewContext} from '../../contexts/ReviewContext';

import Loading from '../../components/Loading/Loading';
import {Prompt} from '../../components/Prompt/Prompt';

import {
  RiskProfileInput,
  RiskProfileSectionInput,
  RiskType,
} from '../../schema-types/globalTypes';
import {UpsertRiskNoteVariables} from '../../schema-types/UpsertRiskNote';
import {UpsertRiskNoteSectionVariables} from '../../schema-types/UpsertRiskNoteSection';
import {useRiskNotesMutations} from './riskNotesQueries';
import {useAutoForm} from '../../components/AutoForm/useAutoForm';
import {RiskProfileFragment} from '../../schema-types/RiskProfileFragment';
import {GetRiskNotes} from '../../schema-types/GetRiskNotes';
import {
  RiskNotesFormProps,
  riskNotesFormSectionType,
  riskNotesFormType,
} from './riskNotesFields';
import {RiskNotesEditor} from './RiskNotesEditor';

import Theme, {themeColors} from '../../theme/Theme';
import {Panel} from '../../components/Panel/Panel';
import {HandoverFooterNavigation} from '../../components/FooterNavigation/HandoverFooterNavigation';
import {UPDATE_CONTACT} from '../../components/AccountOverview/accountOverviewQueries';
import {UpdateContact, UpdateContactVariables} from '../../schema-types/UpdateContact';
import {NoExtraProperties} from '../../utils/typescriptUtils';
import {RiskNotesNoChange, useRiskNotesNoChanges} from './RiskNotesNoChange';
import {RiskNotesOutcomeContact} from './RiskNotesOutcomeContact';
import {RiskNotesComplianceContact} from './RiskNotesComplianceContact';
import {FeatureBoundary} from '../../contexts/FeatureBoundary';
import withAutoFormNew, {FormReadOnly} from '../../components/AutoForm/AutoFormNew';
import {sortAccountContacts} from '../../components/AccountOverview/sortAccountContacts';
import saltus from '../../shared/logger';
import {
  getListOfErrors,
  listErrorMessagesAsNodes,
} from '../recommendations/CreateRecommend/recommendValidation';
import {camelCaseToCapitalisedString} from '../../utils/camelCaseToCapitalisedString';
import {uuidRegex} from '../../components/PrecursorForm/uuidRegex';
import {
  riskNotesFormToVariables,
  riskSegmentsOrderedByBusinessLogic,
  shouldHideRiskOutcomeSections,
} from './RiskNotesFormHelpers';
import {validateRiskNotes} from './validateRiskNotes';
import {UPDATES} from '../../shared/UPDATES';
import {RiskNoteContainer} from './RiskNote';
import {useOnUpdate} from '../../hooks/useOnUpdate';
import {ErrorPage} from '../../pages/ErrorPage';
import {RiskNotesAtrNotes} from './RiskNotesAtrNotes';
import AutoField, {ActionType} from '../../components/AutoForm/AutoFieldNew';

export const riskSpacing = 4;

export const RiskNotesFormWrappedContainer: React.FC<{
  riskData?: GetRiskNotes;
  print: boolean;
}> = ({riskData, print}) => {
  const {reviewDetails, reviewLoading, isFullySubmitted} = useReviewContext();
  const {account, accountLoading} = useAccountContext();

  const {
    upsertRiskProfile,
    upsertRiskProfileError,
    upsertRiskProfileLoading,
    upsertRiskSection,
    upsertRiskSectionError,
    upsertRiskSectionLoading,
    saveDummyRiskProfile,
  } = useRiskNotesMutations();

  // State counter to force re-render of RiskNotesFormWrapped

  const [updateContactMutation] = useMutation<UpdateContact, UpdateContactVariables>(
    UPDATE_CONTACT
  );

  if (reviewLoading) return <Loading entityName="review" />;
  if (accountLoading) {
    return <Loading entityName="account" />;
  }
  if (!account) {
    return (
      <Prompt variant="error" title="Error loading account" content="Account not found" />
    );
  }
  if (!reviewDetails) {
    return (
      <Prompt variant="error" title="Error loading review" content="Review not found" />
    );
  }

  if (!riskData?.reviewDetails) {
    return (
      <Prompt
        variant="error"
        title="Error loading review and risk data"
        content="Review and risk data not found"
      />
    );
  }

  return (
    <>
      <RiskNotesFormWrapped
        print={print}
        accountId={account.id}
        risk={riskData}
        debugChildren={
          <>
            <JsonViewer value={riskData || {}} editable={false} />
          </>
        }
        formFields={[]}
        readOnly={isFullySubmitted}
        riskProfiles={riskData?.reviewDetails?.riskProfiles}
        upsertRiskProfile={(variables) => upsertRiskProfile({variables})}
        validateOnBlur={false}
        upsertRiskProfileLoading={upsertRiskProfileLoading}
        upsertRiskProfileError={upsertRiskProfileError}
        upsertRiskProfileSections={(variables, options) =>
          upsertRiskSection({
            variables,
            ...(options?.skipRefetch ? {refetchQueries: []} : {}),
          })
        }
        upsertRiskSectionsLoading={upsertRiskSectionLoading}
        upsertRiskSectionsError={upsertRiskSectionError}
        initialValueOverrides={riskProfileToFormValues(riskData)}
        enableReinitialize={false}
        submit={async (values) => {
          const variables = riskNotesFormToVariables(
            riskData?.reviewDetails?.contacts?.map((contact) => contact.id) || [],
            values,
            riskData?.reviewDetails?.id || ''
          );
          await upsertRiskProfile({
            variables,
          });
          for (const contact of riskData?.reviewDetails?.contacts || []) {
            const contactId = contact.id;
            const updateContactVariables: UpdateContactVariables = {
              contactId,
              giaHasLiquidityNeeds: values.giaHasLiquidityNeeds[contactId] || false,
              pensionHasLiquidityNeeds:
                values.pensionHasLiquidityNeeds[contactId] || false,
            };
            updateContactMutation({
              variables: updateContactVariables,
            });
          }
        }}
      />
    </>
  );
};

function getSection(
  riskType: RiskType,
  riskProfiles?: RiskProfileFragment[]
): riskNotesFormSectionType {
  if (!riskProfiles?.length) {
    return {
      riskType,
      notes: '',
      noChange: null,
    };
  }
  const firstSection = riskProfiles[0].sections?.find((s) => s.riskType === riskType);
  return {
    riskType,
    notes: firstSection?.notes || '',
    noChange: firstSection?.noChange ?? null,
  };
}

const getCanMarkNoRiskDiscussion = (values: riskNotesFormType) =>
  riskSegmentsOrderedByBusinessLogic.every(
    (key) => values[key].notes.length === 0 || values[key].noChange
  );

export const riskProfileToFormValues = (
  data: GetRiskNotes | undefined
): riskNotesFormType => {
  const riskProfiles = data?.reviewDetails?.riskProfiles;
  const contactSpecific: Pick<
    riskNotesFormType,
    'giaHasLiquidityNeeds' | 'pensionHasLiquidityNeeds' | 'outcomeRisk'
  > = {giaHasLiquidityNeeds: {}, pensionHasLiquidityNeeds: {}, outcomeRisk: {}};
  data?.reviewDetails?.contacts?.forEach((contact) => {
    const riskProfile = riskProfiles?.find((p) => p.contact.id === contact.id);
    contactSpecific.giaHasLiquidityNeeds[contact.id] = contact.giaHasLiquidityNeeds;

    contactSpecific.pensionHasLiquidityNeeds[contact.id] =
      contact.pensionHasLiquidityNeeds;

    contactSpecific.outcomeRisk[contact.id] = {
      score: riskProfile?.outcomeRiskScore || null,
      notes: riskProfile?.outcomeRiskNotes || '',
      riskToleranceQuestionnaireDeclineReason:
        riskProfile?.riskToleranceQuestionnaireDeclineReason || '',

      isRiskToleranceQuestionnaireDeclined: Boolean(
        riskProfile?.isRiskToleranceQuestionnaireDeclined
      ),
    };
  });
  const initialRiskValues: riskNotesFormType = {
    ...contactSpecific,
    [RiskType.KNOWLEDGE_AND_BEHAVIOUR]: getSection(
      RiskType.KNOWLEDGE_AND_BEHAVIOUR,
      riskProfiles
    ),
    [RiskType.VALUES_AND_SERVICE]: getSection(RiskType.VALUES_AND_SERVICE, riskProfiles),
    [RiskType.FINANCIAL_CAPACITY]: getSection(RiskType.FINANCIAL_CAPACITY, riskProfiles),

    centralPropositionAgreed:
      riskProfiles?.every((x) => x.centralPropositionAgreed) ?? undefined,
    dfmAgreed: riskProfiles?.every((x) => x.dfmAgreed) ?? undefined,
    noChangeReasonNotes: riskProfiles?.[0]?.noChangeReasonNotes || '',
    hasNoRiskDicussion: Boolean(riskProfiles?.[0]?.hasNoRiskDicussion),
  };
  return initialRiskValues;
};

const RiskNotesForm: React.FC<RiskNotesFormProps> = (props) => {
  const {
    riskProfiles,
    upsertRiskProfileSections,
    risk,
    print,
    accountId,
    readOnly = false,
  } = props;
  const {values, submitForm, isSubmitting, validateForm, setErrors} =
    useAutoForm<riskNotesFormType>();
  const {reviewDetails} = useReviewContext();
  const [showSegments, setShowSegments] = useState(() => {
    if (!riskProfiles?.length) return true;
    return !riskProfiles?.every((profile) => profile.hasNoRiskDicussion);
  });
  const {allNoChanges} = useRiskNotesNoChanges();
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const contacts = risk?.reviewDetails?.contacts || [];

  // If either contact already has an outcome score on the backend, run validation
  // on mount so that any missing companion fields (e.g. outcome notes) light up
  // immediately instead of waiting for the user to interact.
  const hasExistingOutcomeScoreOnMount = useRef(
    (riskProfiles ?? []).some((rp) => isNumber(rp.outcomeRiskScore))
  );
  useEffect(() => {
    if (hasExistingOutcomeScoreOnMount.current) {
      setErrors(validateRiskNotes(contacts, values, {}));
    }
    // Intentionally run only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canMarkNoRiskDiscussion = useMemo(
    () => getCanMarkNoRiskDiscussion(values),
    [values]
  );

  async function handleSubmit() {
    closeSnackbar();
    return validateForm(values).then(async (errors) => {
      if (!errors || Object.keys(errors).length === 0) {
        saltus.log({'Finalising values': values});
        addBreadcrumb({
          message: 'Checking risk values',
          data: values,
        });
        await submitForm();
        // never turn off finalising if successful to keep it looking tidy
      } else {
        enqueueSnackbar(
          listErrorMessagesAsNodes(getListOfErrors(errors, formatErrorKeys)),
          {
            variant: 'error',
            autoHideDuration: null,
          }
        );
        addBreadcrumb({
          message: 'Risk validation failed',
          data: {values, errors},
        });
        return false; // causes it not to go to next
      }
    });
  }

  const halfWidth = (contacts.length || 0) > 1;

  if (!reviewDetails) {
    return <ErrorPage code="404" text="Review not found" />;
  }

  return (
    <FormReadOnly readOnly={readOnly}>
      <Grid container spacing={4} justifyContent="stretch">
        {sortAccountContacts(accountId!, contacts)?.map((contact) => {
          return (
            <Grid
              xs={halfWidth ? 6 : 12}
              key={contact.id}
              mb={4}
              item
              data-testid={`risk-grid-item-${contact.id}`}
            >
              <Box display="flex" flexDirection="column" height="100%">
                <Panel
                  sx={{height: '100%', flexGrow: 1}}
                  color={themeColors.amber}
                  title={`${contact.simplifiedName}'s Risk Profile Summary`}
                  className="contact-risk"
                  childrenContainerStyle={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                  }}
                >
                  <RiskNoteContainer
                    halfWidth={halfWidth}
                    riskProfile={riskProfiles?.find(
                      (profile) => profile.contact.id === contact.id
                    )}
                    readOnly={readOnly}
                    reviewId={risk.reviewDetails!.id!}
                    accountId={accountId!}
                    contact={contact}
                  />
                </Panel>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <RiskNotesNoDiscussion
        {...props}
        setShowSegments={setShowSegments}
        canMarkNoRiskDiscussion={canMarkNoRiskDiscussion}
      />

      {showSegments && (
        <>
          <Box width="100%" pt={4} />
          <Panel color={themeColors.amber} title="Risk Discussion">
            <RiskNotesEditor
              print={print}
              readOnly={readOnly}
              risk={risk}
              riskTypes={riskSegmentsOrderedByBusinessLogic}
              onSectionChange={(riskType, val, options) => {
                const variables: NoExtraProperties<UpsertRiskNoteSectionVariables> = {
                  inputs: [],
                };
                contacts?.forEach((contact) => {
                  const input: NoExtraProperties<RiskProfileSectionInput> = {
                    reviewId: risk.reviewDetails?.id,
                    contactId: contact.id,
                    notes: val.notes,
                    noChange: Boolean(val.noChange),
                    riskType,
                  };
                  variables.inputs.push(input);
                });

                upsertRiskProfileSections(variables, options);
              }}
            />
          </Panel>
          <Box width="100%" pt={4} />
        </>
      )}
      {/* Liquidity needs is independent of the risk-discussion toggle: it must
          stay visible even when "No risk discussion" is selected. */}
      <Box width="100%" pt={4} />
      <Panel color={themeColors.amber} title="Liquidity needs">
        {sortAccountContacts(accountId, contacts)?.map((contact) => {
          return (
            <Box
              p={2}
              mb={2}
              key={contact.id}
              borderBottom={`1px solid ${Theme.palette.grey[300]}`}
            >
              <RiskNotesComplianceContact
                print={print}
                risk={risk}
                contactId={contact.id}
                riskProfiles={riskProfiles}
              />
            </Box>
          );
        })}
      </Panel>
      <Box width="100%" pt={4} />
      {shouldHideRiskOutcomeSections({
        hasNoRiskDicussion: values.hasNoRiskDicussion,
        allNoChanges,
      })
        ? null
        : sortAccountContacts(accountId, contacts)?.map((contact) => {
            return (
              <>
                <Box width="100%" pt={4} />
                <RiskNotesOutcomeContact
                  print={print}
                  key={contact.id}
                  contactId={contact.id}
                  risk={risk}
                />
              </>
            );
          })}
      <Box width="100%" pt={4} />
      <Panel color={themeColors.amber} title="Previous attitude to risk notes">
        <RiskNotesAtrNotes />
      </Panel>
      <Box width="100%" pt={4} />
      <HandoverFooterNavigation
        onNext={() => handleSubmit()}
        onNextLoading={isSubmitting}
        onExit={() => handleSubmit()}
        exitingLoading={isSubmitting}
        nextIcon={<TrendingUp />}
      />
    </FormReadOnly>
  );
};

export const RiskNotesNoDiscussion: React.FC<
  RiskNotesFormProps & {
    setShowSegments: (show: boolean) => void;
    canMarkNoRiskDiscussion: boolean;
  }
> = ({risk, upsertRiskProfile, setShowSegments, canMarkNoRiskDiscussion}) => {
  const contacts = risk?.reviewDetails?.contacts || [];
  const {values, setFieldValue} = useAutoForm<riskNotesFormType>();
  const {allNoChanges: allSectionsNoChange} = useRiskNotesNoChanges();

  useOnUpdate(() => {
    if (values.hasNoRiskDicussion) {
      // this NEEDS to run even on page load
      riskSegmentsOrderedByBusinessLogic.forEach(async (riskType) => {
        await setFieldValue(UPDATES._1010, `${riskType}.noChange`, true);
        setShowSegments(false);
      });
    } else {
      riskSegmentsOrderedByBusinessLogic.forEach(async (riskType) => {
        await setFieldValue(UPDATES._1010, `${riskType}.noChange`, false);
        setShowSegments(true);
      });
    }
    const variables: NoExtraProperties<UpsertRiskNoteVariables> = {
      inputs: [],
    };
    contacts?.forEach((contact) => {
      const input: NoExtraProperties<RiskProfileInput> = {
        reviewId: risk.reviewDetails?.id,
        contactId: contact.id,
        hasNoRiskDicussion: values.hasNoRiskDicussion,
      };
      variables.inputs.push(input);
    });
    upsertRiskProfile(variables);
  }, [values.hasNoRiskDicussion]);

  const showJustification = values.hasNoRiskDicussion || allSectionsNoChange;
  return (
    <>
      <Panel color={themeColors.amber} title="Risk Discussion Required?">
        <Box p={1}>
          <AutoField
            field={{
              id: 'hasNoRiskDicussion',
              type: ActionType.checkboxToggle,
              label: 'No risk discussion',
              disabled: !canMarkNoRiskDiscussion,
            }}
          />

          {!canMarkNoRiskDiscussion && (
            <Alert severity="warning" variant="outlined">
              Unable to mark as no risk discussion while risk discussion notes exist.
              Please clear the notes or mark each section as no change before selecting
              this option.
            </Alert>
          )}
        </Box>
        {showJustification ? (
          <RiskNotesNoChange
            onChange={(val) => {
              const variables: NoExtraProperties<UpsertRiskNoteVariables> = {
                inputs: [],
              };
              contacts?.forEach((contact) => {
                const input: NoExtraProperties<RiskProfileInput> = {
                  reviewId: risk.reviewDetails?.id,
                  contactId: contact.id,
                  noChangeReasonNotes: val.noChangeReasonNotes,
                  hasNoRiskDicussion: (val.noChangeReasonNotes?.length ?? 0) > 0,
                };
                variables.inputs.push(input);
              });
              upsertRiskProfile(variables);
            }}
          />
        ) : null}
      </Panel>
    </>
  );
};

const RiskNotesFormWrapped = withAutoFormNew<riskNotesFormType, RiskNotesFormProps>(
  RiskNotesForm
);

function formatErrorKeys(keys: string) {
  return keys
    .split('.')
    .map((txt) =>
      camelCaseToCapitalisedString(txt).replace(uuidRegex, '').replace(/_/g, ' ')
    )
    .filter((x) => x) // remove empty strings
    .join(' ');
}
